import { graphql } from 'gatsby'
import ImagesLoaded from 'imagesloaded'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import initTealiumTracker from 'tealium-tracker'
import { StoreDispatch } from '../Store'
import { teliumDefaults } from '../tracking/telium'

// Components
import AccordionSection from '../components/AccordionSection'
import Amenities from '../components/Amenities'
import CarouselTextSplit from '../components/CarouselTextSplit'
import ExpeditionHero from '../components/ExpeditionHero'
import Footer from '../components/Footer'
import FullWidthCarousel from '../components/FullWidthCarousel'
import Seo from '../components/Seo'
import { animation } from '../styles/vars/animation.style'
import { getExpeditionColors, scrollToId } from '../utils/utils'

// Styles
import AmenitiesCompact from '../components/AmenitiesCompact'
import AmenitiesTips from '../components/AmenitiesTips'
import BookingBanner from '../components/BookingBanner'
import ImageContentFixed from '../components/ImageContentFixed'
import NextUp from '../components/NextUp'
import NextUpEnd from '../components/NextUpEnd'
import Region from '../components/Region'
import { colors } from '../styles/vars/colors.style'

const ExpeditionPage = ({ data, location }) => {
  const wrapperRef = useRef()

  const {
    contentful_id,
    id: expeditionId,
    slug,
    expeditionDates,
    expeditionNumber,
    title,
    introVideoUrl,
    introVideoPlaceholder,
    introHeading,
    introText,
    introButton,
    pageContent,
    bookingGallery,
    seoTitle,
    seoDescription,
    seoImage,
  } = data.contentfulPageExpedition

  const {
    id: bookingBannerId,
    title: bookingBannerTitle,
    ctaText,
    ctaUrl,
  } = data.contentfulBookingBanner

  const { expeditions } = data.contentfulPageExpeditions

  const nextExpedition = useMemo(() => {
    const currentExpeditionIndex = expeditions.reduce(
      (accumulator, expeditionItem, expeditionIndex) => {
        if (expeditionItem.id === expeditionId) {
          accumulator = expeditionIndex
        }
        return accumulator
      },
      null
    )

    if (currentExpeditionIndex === expeditions.length - 1) return null

    const nextExpeditionIndex = currentExpeditionIndex + 1

    return expeditions[nextExpeditionIndex]
  }, [expeditions, expeditionId])

  const expeditionColors = getExpeditionColors(expeditionNumber)

  const { previousSlide, nextSlide } = data.contentfulGlobalSettings

  const dispatch = useContext(StoreDispatch)

  useEffect(() => {
    const { trackPageLoad } = initTealiumTracker()

    trackPageLoad({
      pageName: `ultimatecruise:expeditions-${expeditionNumber}`,
      ...teliumDefaults,
    })
  }, [dispatch, expeditionNumber])

  const loaded = useCallback(() => {
    setTimeout(window.scroll.update, 750)
  }, [])

  useEffect(() => {
    const imageLoadTrack = new ImagesLoaded(wrapperRef.current)

    imageLoadTrack.on('always', loaded)

    return () => {
      imageLoadTrack.off('always', loaded)
    }
  }, [loaded])

  useEffect(() => {
    const query = location.search

    const timeout = setTimeout(() => {
      if (query.length) {
        const id = query.substring(1).split('&')[0]
        scrollToId(id, dispatch, location)
      }
    }, (animation.maskShowDuration + animation.maskHideDuration) * 2000)

    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
  }, [location, dispatch])

  let fixedImageCount = 0

  return (
    <div ref={wrapperRef}>
      <Seo
        title={seoTitle}
        description={seoDescription}
        image={seoImage.file.url}
        pathname={`/expeditions/${slug}`}
      />

      <ExpeditionHero
        id={`${contentful_id}-hero`}
        backgroundColor={expeditionColors.light}
        labelColor={expeditionColors.dark}
        expeditionDates={expeditionDates}
        expeditionNumber={expeditionNumber}
        title={title}
        videoUrl={introVideoUrl}
        placeholder={introVideoPlaceholder}
        heading={introHeading}
        text={introText}
        button={introButton}
      />

      {pageContent.map((section, key) => {
        if (section.__typename === `ContentfulSectionAccordion`) {
          return (
            <AccordionSection
              id={section.contentful_id}
              heading={section.heading}
              content={section.accordionItems}
              key={key}
              button={section.button}
              linkId={section.sectionId}
              faq={section.faqSection}
              bottomPadding={true}
              defaultOpen={1}
              background={expeditionColors.light}
            />
          )
        }

        if (section.__typename === 'ContentfulSectionFixedImage') {
          fixedImageCount++
          return (
            <ImageContentFixed
              id={`ImageContentFixed-${key}`}
              key={key}
              content={section.content}
              linkId={section.sectionId}
              alignLeft={section.contentAlign}
              image={section.fixedImage}
              background={
                fixedImageCount === 2
                  ? expeditionColors.dark
                  : fixedImageCount === 3
                  ? expeditionColors.light
                  : null
              }
              color={fixedImageCount === 2 ? colors.light : null}
            />
          )
        }

        if (section.__typename === 'ContentfulSectionRegion') {
          return (
            <Region
              id={`ContentfulSectionRegion-${key}`}
              key={key}
              linkId={section.sectionId}
              dates={section.dates}
              title={section.title}
              description={section.description}
              labelColor={expeditionColors.dark}
              backgroundImage={section.backgroundImage}
              maxWidth={section.maxWidth}
            />
          )
        }

        if (section.__typename === `ContentfulSectionIconList`) {
          if (section.sectionId.indexOf('amenities') > -1) {
            return (
              <AmenitiesCompact
                id={section.contentful_id}
                heading={section.heading}
                text={section.copy}
                disclaimer={section.smallText}
                listItems={section.listItems}
                key={key}
                linkId={section.sectionId}
                anchorButtonLabel={`Check out pricing`}
                anchorButtonID={`faq-item-1`}
              />
            )
          }

          if (section.sectionId.indexOf('insider') > -1) {
            return (
              <AmenitiesTips
                id={section.contentful_id}
                heading={section.heading}
                text={section.copy}
                disclaimer={section.smallText}
                listItems={section.listItems}
                key={key}
                linkId={section.sectionId}
              />
            )
          }

          return (
            <Amenities
              id={section.contentful_id}
              heading={section.heading}
              text={section.copy}
              disclaimer={section.smallText}
              listItems={section.listItems}
              key={key}
              linkId={section.sectionId}
            />
          )
        }

        // if (section.__typename === `ContentfulSectionDoubleImageBanner`) {
        //   return null
        // }

        if (section.__typename === `ContentfulSectionFullWidthCarousel`) {
          return (
            <FullWidthCarousel
              id={section.contentful_id}
              heading={section.heading}
              carouselItems={section.carouselItems}
              key={key}
              linkId={section.sectionId}
              nextSlideLabel={nextSlide}
              prevSlideLabel={previousSlide}
            />
          )
        }

        if (section.__typename === `ContentfulSectionCarouselTextSplit`) {
          return (
            <CarouselTextSplit
              id={section.contentful_id}
              text={section.text}
              carouselItems={section.carouselItems}
              key={key}
              linkId={section.sectionId}
              nextSlideLabel={nextSlide}
              prevSlideLabel={previousSlide}
              background={expeditionColors.dark}
              color={colors.light}
            />
          )
        }

        return ``
      })}

      <BookingBanner
        id={bookingBannerId}
        title={bookingBannerTitle}
        ctaText={ctaText}
        ctaUrl={ctaUrl}
        background={expeditionColors.dark}
        gallery={bookingGallery}
      />

      {nextExpedition ? (
        <NextUp
          id={expeditionId}
          backgroundImage={nextExpedition.introVideoPlaceholder.gatsbyImageData}
          number={nextExpedition.expeditionNumber}
          labelColor={
            getExpeditionColors(nextExpedition.expeditionNumber)?.dark
          }
          title={nextExpedition.title}
          to={`/expeditions/${nextExpedition.slug}`}
        />
      ) : (
        <NextUpEnd
          id={expeditionId}
          number={`1-4`}
          title={`All four corners, one epic voyage`}
          to={`/expeditions`}
        />
      )}

      <Footer />
    </div>
  )
}

export default ExpeditionPage

export const expeditionQuery = graphql`
  query ExpeditionPageQuery($slug: String!) {
    contentfulPageExpedition(slug: { eq: $slug }) {
      contentful_id
      id
      slug
      seoTitle
      seoDescription
      seoImage {
        file {
          url
        }
      }
      expeditionDates
      expeditionNumber
      title
      introVideoUrl
      introVideoPlaceholder {
        description
        gatsbyImageData
      }
      introHeading {
        raw
      }
      introText {
        introText
      }
      introButton {
        ... on ContentfulItemInternalLink {
          internalLink: link {
            ... on ContentfulPageExpedition {
              slug
            }
            ... on ContentfulPageItinerary {
              slug
            }
            ... on ContentfulPageExpedition {
              slug
            }
          }
          label
          anchorIdOptional
        }
        ... on ContentfulItemExternalLink {
          externalLink: link
          label
        }
      }
      pageContent {
        __typename
        ... on ContentfulSectionFixedImage {
          contentful_id
          sectionId
          contentAlign
          content {
            raw
            references {
              __typename
              ... on ContentfulAsset {
                contentful_id
                description
                gatsbyImageData
              }
            }
          }
          fixedImage {
            description
            gatsbyImageData
            title
          }
        }
        ... on ContentfulSectionRegion {
          contentful_id
          sectionId
          title {
            raw
            references {
              __typename
              ... on ContentfulAsset {
                contentful_id
                gatsbyImageData
                file {
                  details {
                    image {
                      width
                      height
                    }
                  }
                }
              }
            }
          }
          dates
          description
          backgroundImage {
            contentful_id
            gatsbyImageData(width: 1920)
            description
            fluid(maxWidth: 1920) {
              srcSet
            }
          }
          maxWidth
        }
        ... on ContentfulSectionAccordion {
          contentful_id
          heading {
            raw
          }
          sectionId
          faqSection
          accordionItems {
            title
            content {
              raw
              references {
                __typename
                ... on ContentfulItemTable {
                  contentful_id
                  __typename
                  table {
                    tableData
                  }
                }
              }
            }
          }
          button {
            ... on ContentfulItemInternalLink {
              internalLink: link {
                ... on ContentfulPageExpedition {
                  slug
                }
                ... on ContentfulPageItinerary {
                  slug
                }
                ... on ContentfulPageExpedition {
                  slug
                }
              }
              label
            }
            ... on ContentfulItemExternalLink {
              externalLink: link
              label
            }
          }
        }
        ... on ContentfulSectionIconList {
          contentful_id
          id
          sectionId
          heading {
            raw
          }
          copy {
            raw
          }
          smallText
          listItems {
            icon {
              gatsbyImageData(width: 200, height: 200)
            }
            iconSvg {
              file {
                url
                details {
                  image {
                    width
                  }
                }
              }
              description
            }
            title
            text {
              text
            }
          }
        }
        ... on ContentfulSectionCarouselTextSplit {
          contentful_id
          sectionId
          text {
            raw
          }
          carouselItems {
            title
            label
            image {
              gatsbyImageData(quality: 40, width: 592, height: 777)
              description
            }
            featuredCaption {
              raw
            }
            link {
              ... on ContentfulItemInternalLink {
                internalLink: link {
                  ... on ContentfulPageExpedition {
                    slug
                  }
                  ... on ContentfulPageItinerary {
                    slug
                  }
                  ... on ContentfulPageExpedition {
                    slug
                  }
                }
                label
                anchorIdOptional
              }
              ... on ContentfulItemExternalLink {
                externalLink: link
                label
              }
            }
          }
        }
        ... on ContentfulSectionDoubleImageBanner {
          contentful_id
          sectionId
          date
          plainTitle
          smallImage {
            gatsbyImageData(height: 210)
            description
          }
          copy {
            raw
          }
          backgroundImage {
            title
            gatsbyImageData(width: 3840, height: 2040, quality: 35)
          }
        }
        ... on ContentfulSectionFullWidthCarousel {
          contentful_id
          sectionId
          heading {
            raw
          }
          carouselItems {
            title
            label
            image {
              gatsbyImageData(quality: 75)
              description
            }
            featuredCaption {
              raw
            }
            link {
              ... on ContentfulItemInternalLink {
                internalLink: link {
                  ... on ContentfulPageExpedition {
                    slug
                  }
                  ... on ContentfulPageItinerary {
                    slug
                  }
                  ... on ContentfulPageExpedition {
                    slug
                  }
                }
                label
                anchorIdOptional
              }
              ... on ContentfulItemExternalLink {
                externalLink: link
                label
              }
            }
          }
        }
        ... on ContentfulSectionAccordion {
          contentful_id
          heading {
            raw
          }
          sectionId
          faqSection
          accordionItems {
            title
            content {
              raw
              references {
                __typename
                ... on ContentfulItemTable {
                  contentful_id
                  __typename
                  table {
                    tableData
                  }
                }
              }
            }
          }
          button {
            ... on ContentfulItemInternalLink {
              internalLink: link {
                ... on ContentfulPageExpedition {
                  slug
                }
                ... on ContentfulPageItinerary {
                  slug
                }
                ... on ContentfulPageExpedition {
                  slug
                }
              }
              label
            }
            ... on ContentfulItemExternalLink {
              externalLink: link
              label
            }
          }
        }
      }
      bookingGallery {
        gatsbyImageData
        description
      }
    }
    contentfulGlobalSettings {
      viewFullItinerary
      viewDetailedItinerary
      exploreTheDetails
      previousSlide
      nextSlide
    }
    contentfulBookingBanner {
      id
      title {
        raw
      }
      ctaText
      ctaUrl
    }
    contentfulPageExpeditions {
      expeditions {
        id
        title
        slug
        introVideoPlaceholder {
          gatsbyImageData
        }
        expeditionNumber
      }
    }
  }
`
